import { useEffect, useRef, useState } from "preact/hooks";
import type { App } from "../app.ts";
import { useAppState } from "./hooks.ts";
import { IconButton } from "./icon.tsx";

const onMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** What changes how the note is seen or kept: details, preview, view, saving, export. */
export function Actions({ app, canFocus }: { app: App; canFocus: boolean }) {
  const s = useAppState(app);
  const has = Boolean(s.current);
  const [menu, setMenu] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);

  // The menu closes on Escape and on a press anywhere outside it.
  useEffect(() => {
    if (!menu) return;
    const away = (e: Event) => {
      if (!wrap.current?.contains(e.target as Node)) setMenu(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setMenu(false);
      opener.current?.focus();
    };
    addEventListener("pointerdown", away);
    addEventListener("keydown", onEscape, true);
    return () => {
      removeEventListener("pointerdown", away);
      removeEventListener("keydown", onEscape, true);
    };
  }, [menu]);

  const dark = app.effectiveTheme() === "dark";
  const viewOn = s.focus;

  return (
    <div class="actions-wrap" ref={wrap}>
      <div class="island top actions" role="toolbar" aria-label="Actions">
        <IconButton
          icon="search"
          label="Search and commands"
          tip={`Search and commands (${onMac ? "Cmd" : "Ctrl"}+Shift+K)`}
          class="palette-button"
          aria-haspopup="dialog"
          aria-expanded={s.paletteOpen}
          on={s.paletteOpen}
          onClick={() => app.setPalette(true)}
        />
        <IconButton
          icon="info"
          label="Details"
          on={s.detailsOpen}
          disabled={!has}
          aria-expanded={s.detailsOpen}
          aria-controls="details"
          onClick={() => app.setDetails(!s.detailsOpen)}
        />
        <IconButton
          icon="preview"
          label="Preview"
          tip="Preview as the site"
          on={s.previewOpen}
          disabled={!has}
          aria-expanded={s.previewOpen}
          aria-controls="preview"
          onClick={() => app.setPreview(!s.previewOpen)}
        />
        <IconButton
          icon="view"
          label="View"
          on={viewOn || menu}
          aria-haspopup="true"
          aria-expanded={menu}
          aria-controls="view-menu"
          aria-pressed={viewOn}
          buttonRef={opener}
          onClick={() => setMenu(!menu)}
        />
        <span class="rule" />
        <IconButton
          icon="autosave"
          label="Autosave"
          tip={s.autosave ? "Autosave on" : "Autosave off"}
          on={s.autosave}
          aria-pressed={s.autosave}
          onClick={() => app.setAutosave(!s.autosave)}
        />
        {!s.autosave && (
          <IconButton
            icon="save"
            label="Save"
            tip={`Save (${onMac ? "Cmd" : "Ctrl"}+S)`}
            disabled={!has}
            onClick={() => app.save()}
          />
        )}
        <IconButton
          icon="export"
          label="Export"
          aria-expanded={s.exportOpen}
          aria-controls="export"
          on={s.exportOpen}
          onClick={() => app.setExport(!s.exportOpen)}
        />
      </div>
      <div id="view-menu" class="island menu" role="menu" aria-label="View" hidden={!menu}>
        <p class="menu-title">View</p>
        <MenuSwitch
          label="Focus"
          hint="Dim everything but the paragraph"
          checked={s.focus}
          disabled={!has || !canFocus}
          onToggle={() => app.setFocus(!s.focus)}
        />
        <MenuSwitch
          label="Typewriter"
          hint={s.focus ? "Keep the line at the middle" : "Works with Focus"}
          checked={s.typewriter}
          disabled={!s.focus}
          onToggle={() => app.setTypewriter(!s.typewriter)}
        />
        <div class="menu-sep" />
        <MenuSwitch label="Dark theme" checked={dark} onToggle={() => app.toggleTheme()} />
      </div>
    </div>
  );
}

function MenuSwitch({
  label,
  hint,
  checked,
  disabled = false,
  onToggle,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      class="menu-row"
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={onToggle}
    >
      <span>
        {label}
        {hint && <small>{hint}</small>}
      </span>
      <span class="switch" aria-hidden="true" />
    </button>
  );
}
